import {
  boolean,
  index,
  integer,
  pgEnum,
  pgTable,
  primaryKey,
  text,
  timestamp,
  uuid,
} from "drizzle-orm/pg-core";
import { createSchemaFactory } from "drizzle-zod";
import { z } from "zod";

import { users } from "./auth";
import { playerProfiles } from "./player-profiles";
import { timestamps } from "./shared";

const { createInsertSchema, createSelectSchema } = createSchemaFactory({
  zodInstance: z,
});

/**
 * Role in a user-profile relationship.
 *
 * - self: This is the user's own profile
 * - guardian: User is a parent/guardian managing this profile (e.g., for a minor)
 */
export const profileRoleEnum = pgEnum("profile_role", ["self", "guardian"]);

export const profileRoleSchema = createSelectSchema(profileRoleEnum);

export type ProfileRole = z.infer<typeof profileRoleSchema>;

/**
 * Many-to-many relationship between users and player profiles.
 *
 * This allows:
 * - A user to have multiple profiles (their own + dependents)
 * - A profile to be accessible by multiple users (player + their guardian)
 *
 * The `role` field indicates the relationship type.
 * When a profile has role="self", that user IS the player.
 * When a profile has role="guardian", the user manages the profile for someone else.
 */
export const userProfiles = pgTable(
  "user_profiles",
  {
    userId: text()
      .references(() => users.id, { onDelete: "cascade" })
      .notNull(),
    playerProfileId: integer()
      .references(() => playerProfiles.id, { onDelete: "cascade" })
      .notNull(),

    // Relationship type
    role: profileRoleEnum().notNull().default("self"),

    // Can this user edit the profile?
    canEdit: boolean().notNull().default(true),

    ...timestamps,
  },
  (table) => [
    primaryKey({ columns: [table.userId, table.playerProfileId] }),
    index("user_profiles_user_id_idx").on(table.userId),
    index("user_profiles_player_profile_id_idx").on(table.playerProfileId),
    index("idx_user_profiles_user_role").on(table.userId, table.role),
  ],
);

export const selectUserProfileSchema = createSelectSchema(userProfiles);
export const insertUserProfileSchema = createInsertSchema(userProfiles);

export type UserProfile = z.infer<typeof selectUserProfileSchema>;

/**
 * Legacy users table for migration.
 *
 * Stores original user data from the legacy gel database.
 * Used during login to prompt users to claim their profiles.
 */
export const legacyUsers = pgTable(
  "legacy_users",
  {
    // Original legacy user ID from gel
    id: uuid().primaryKey(),

    // Original contact info (not unique - many legacy users shared these)
    email: text(),
    normalizedEmail: text(),
    phone: text(),
    normalizedPhone: text(),

    // Original username from gel
    username: text().notNull().unique(),

    // Name from legacy system
    firstName: text().notNull(),
    lastName: text().notNull(),

    // Link to the profile created from this legacy user
    playerProfileId: integer().references(() => playerProfiles.id, {
      onDelete: "set null",
    }),

    // Claim tracking
    claimedByUserId: text().references(() => users.id, {
      onDelete: "set null",
    }),
    claimedAt: timestamp(),

    ...timestamps,
  },
  (table) => [
    index("legacy_users_player_profile_id_idx").on(table.playerProfileId),
    index("legacy_users_claimed_by_user_id_idx").on(table.claimedByUserId),
  ],
);

export const selectLegacyUserSchema = createSelectSchema(legacyUsers);
export const insertLegacyUserSchema = createInsertSchema(legacyUsers);

export type LegacyUser = z.infer<typeof selectLegacyUserSchema>;
